<template>
  <div class="flex flex-col gap-3 text-gray-800 tabular-nums">

    <section class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <header class="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 bg-gray-50">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-600">{{ isAction ? 'Action item' : 'Constraint' }}</h4>
        <router-link
          :to="{ path: `/entity/${constraint.practice_id}/engagement-tracker`, query: { node: constraint.id } }"
          class="text-[12px] text-blue-700 hover:underline"
        >Open in Engagement Tracker</router-link>
      </header>
      <div class="px-4 py-3">
        <div class="flex items-start gap-3">
          <p class="sensitive m-0 flex-1 text-[13px] font-medium text-gray-900 leading-snug" data-demo-blur>{{ constraint.title }}</p>
          <button type="button" class="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:border-blue-500 hover:text-blue-700" @click.stop="openLog(constraint.id)">Log update</button>
        </div>
        <p v-if="constraint.addresses" class="mt-1 mb-0 text-[11px] text-gray-500">Addresses <span class="sensitive font-medium text-gray-700" data-demo-blur>{{ constraint.addresses }}</span></p>
        <p v-if="constraint.description" class="sensitive mt-1.5 mb-0 text-[13px] text-gray-700 leading-relaxed" data-demo-blur>{{ constraint.description }}</p>
        <div v-if="constraint.updates && constraint.updates.length" class="mt-1.5 space-y-1">
          <div v-for="u in constraint.updates" :key="u.id" class="flex items-baseline gap-2 text-[12px]">
            <span class="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">{{ fmtDate(u.observed_at) }}</span>
            <span class="sensitive shrink-0 text-[11px] font-medium text-gray-600">{{ u.user_name }}</span>
            <span class="sensitive text-gray-700" data-demo-blur>{{ u.text }}</span>
          </div>
        </div>
        <div v-if="logForm.nodeId === constraint.id" class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2" @click.stop>
          <textarea
            v-model="logForm.text"
            rows="2"
            maxlength="2000"
            class="w-full rounded border border-gray-300 bg-white px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="What was done?"
          ></textarea>
          <div class="mt-1.5 flex items-center gap-3">
            <label v-if="isAction && constraint.status !== 'resolved'" class="flex items-center gap-1.5 text-[12px] text-gray-700"><input v-model="logForm.complete" type="checkbox"> This completes it</label>
            <span v-if="logForm.error" class="text-[11px] text-red-600">{{ logForm.error }}</span>
            <div class="ml-auto flex items-center gap-2">
              <button type="button" class="px-2.5 py-1 text-[12px] text-gray-600 hover:underline" @click="closeLog">Cancel</button>
              <button type="button" class="rounded-md bg-blue-600 px-3 py-1 text-[12px] font-medium text-white hover:bg-blue-700 disabled:opacity-50" :disabled="!logForm.text.trim() || logForm.saving" @click="saveLog(constraint)">{{ logForm.saving ? 'Saving…' : 'Save update' }}</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="constraint.impact" class="rounded-lg border border-amber-200 bg-white shadow-sm overflow-hidden">
      <header class="flex items-center justify-between gap-3 px-4 py-2 border-b border-amber-100 bg-amber-50">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-amber-900">Modeled impact · {{ impactLabel(constraint.impact.initiative) }}</h4>
        <span class="text-[10px] uppercase tracking-wide text-amber-800">{{ impactBasis(constraint.impact.initiative) === 'tested' ? 'tested lift' : 'estimated lift' }}</span>
      </header>
      <div class="px-4 py-3 grid grid-cols-3 gap-4 text-[12px] text-gray-700">
        <div><div class="text-[11px] text-gray-500">If cleared</div><div class="text-base font-semibold text-gray-900">+{{ constraint.impact.if_cleared.toFixed(1) }}</div><div class="text-[11px] text-gray-500">{{ impactUnit(constraint.impact.initiative) }}</div></div>
        <div><div class="text-[11px] text-gray-500">Practice gain</div><div class="text-base font-semibold text-gray-900">+{{ (constraint.impact.if_cleared / constraint.impact.denominator * 100).toFixed(1) }} pts</div><div class="text-[11px] text-gray-500">on its rate</div></div>
        <div><div class="text-[11px] text-gray-500">Practice now</div><div class="text-base font-semibold text-gray-900">{{ (constraint.impact.rate * 100).toFixed(1) }}%</div><div class="text-[11px] text-gray-500">of {{ fmtInt(constraint.impact.denominator) }}</div></div>
      </div>
    </section>

    <section v-if="constraint.actions && constraint.actions.length" class="rounded-lg border border-blue-200 bg-white shadow-sm overflow-hidden">
      <header class="px-4 py-2 border-b border-blue-100 bg-blue-50">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-blue-800">Action items — {{ openCount }} open<template v-if="constraint.actions.length > openCount"> · {{ constraint.actions.length - openCount }} resolved</template></h4>
      </header>
      <div class="divide-y divide-gray-100">
        <div v-for="a in constraint.actions" :key="a.id" class="px-4 py-1.5">
          <div class="flex items-baseline gap-3">
            <span
              class="w-[84px] shrink-0 text-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              :class="ACTION_STATUS_CLS[a.status] || 'bg-gray-100 text-gray-600'"
            >{{ titleize(a.status) }}</span>
            <router-link
              :to="{ path: `/entity/${constraint.practice_id}/engagement-tracker`, query: { node: a.id } }"
              class="sensitive flex-1 min-w-0 text-[13px] hover:text-blue-700 no-underline"
              :class="a.status === 'resolved' ? 'text-gray-500' : 'text-gray-800'"
              data-demo-blur
            >{{ a.title }}</router-link>
            <span class="w-52 shrink-0 truncate text-[11px] text-gray-500">
              <span class="sensitive">{{ a.owner || 'No owner' }}</span><template v-if="a.owner_group"> · {{ a.owner_group === 'care_team' ? 'Care team' : 'Practice' }}</template>
            </span>
            <span class="w-24 shrink-0 text-[11px] text-gray-500 whitespace-nowrap">{{ fmtDate(a.opened) }}</span>
            <span class="w-24 shrink-0 text-[11px] whitespace-nowrap" :class="a.due_date ? 'text-gray-700' : 'text-gray-300'">{{ a.due_date ? 'Due ' + fmtDate(a.due_date) : '' }}</span>
            <button
              type="button"
              class="shrink-0 text-[14px] leading-none"
              :class="a.prioritized ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'"
              :title="priorityTitle(a)"
              @click.stop="togglePriority(a)"
            >★</button>
            <button type="button" class="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:border-blue-500 hover:text-blue-700" @click.stop="openLog(a.id)">Log update</button>
          </div>
          <div class="pl-[96px]">
            <div v-if="a.updates && a.updates.length" class="mt-1.5 space-y-1">
              <div v-for="u in a.updates" :key="u.id" class="flex items-baseline gap-2 text-[12px]">
                <span class="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">{{ fmtDate(u.observed_at) }}</span>
                <span class="sensitive shrink-0 text-[11px] font-medium text-gray-600">{{ u.user_name }}</span>
                <span class="sensitive text-gray-700" data-demo-blur>{{ u.text }}</span>
              </div>
            </div>
            <div v-if="logForm.nodeId === a.id" class="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2" @click.stop>
              <textarea
                v-model="logForm.text"
                rows="2"
                maxlength="2000"
                class="w-full rounded border border-gray-300 bg-white px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="What was done?"
              ></textarea>
              <div class="mt-1.5 flex items-center gap-3">
                <label v-if="a.status !== 'resolved'" class="flex items-center gap-1.5 text-[12px] text-gray-700"><input v-model="logForm.complete" type="checkbox"> This completes it</label>
                <span v-if="logForm.error" class="text-[11px] text-red-600">{{ logForm.error }}</span>
                <div class="ml-auto flex items-center gap-2">
                  <button type="button" class="px-2.5 py-1 text-[12px] text-gray-600 hover:underline" @click="closeLog">Cancel</button>
                  <button type="button" class="rounded-md bg-blue-600 px-3 py-1 text-[12px] font-medium text-white hover:bg-blue-700 disabled:opacity-50" :disabled="!logForm.text.trim() || logForm.saving" @click="saveLog(a)">{{ logForm.saving ? 'Saving…' : 'Save update' }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="coaching.length" class="rounded-lg border border-teal-200 bg-white shadow-sm overflow-hidden">
      <header class="flex items-center justify-between gap-3 px-4 py-2 border-b border-teal-100 bg-teal-50">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-teal-900">Coaching history — {{ coaching.length }} {{ coaching.length === 1 ? 'meeting' : 'meetings' }}</h4>
      </header>
      <div class="px-4 py-2.5 divide-y divide-gray-100">
        <div v-for="(h, hi) in coaching" :key="hi" class="py-2 first:pt-0 last:pb-0">
          <div class="flex items-center gap-2 flex-wrap text-[11px]">
            <span class="font-semibold text-gray-700">{{ fmtDate(h.date) }}</span>
            <span v-if="h.executor_on_call" class="rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-800">Executor on call</span>
            <span
              v-for="t in proChangeTactics(h)"
              :key="t.tactic"
              class="rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-green-800"
            >{{ PRO_CHANGE_LABELS[t.tactic] || titleize(t.tactic) }}</span>
          </div>
          <p v-if="h.summary" class="sensitive mt-1 mb-0 text-[12px] text-gray-800" data-demo-blur>{{ h.summary }}</p>
          <div v-if="proChangeTactics(h).length" class="mt-1 space-y-1">
            <div v-for="(t, ti) in proChangeTactics(h)" :key="ti" class="text-[11px] text-gray-600">
              <span class="font-medium text-gray-700">{{ PRO_CHANGE_LABELS[t.tactic] || titleize(t.tactic) }}:</span>
              <span class="sensitive italic text-gray-600" data-demo-blur> "{{ t.quote }}"</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="resolved.length" class="rounded-lg border border-green-200 bg-white shadow-sm overflow-hidden">
      <header class="px-4 py-2 border-b border-green-100 bg-green-50">
        <h4 class="m-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-green-800">Solved elsewhere — {{ resolved.length }}</h4>
      </header>
      <div class="divide-y divide-gray-100">
        <div v-for="r in resolved" :key="r.id" class="px-4 py-2.5">
          <router-link
            :to="{ path: `/entity/${r.practice_id}/engagement-tracker`, query: { node: r.id } }"
            class="sensitive block text-[13px] font-medium text-gray-900 hover:text-blue-700 no-underline"
            data-demo-blur
          >{{ r.title }}</router-link>
          <div class="mt-0.5 text-[11px] text-gray-500">
            <span class="sensitive">{{ r.practice }}</span>
            <template v-if="r.manager"> · Team Member <span class="sensitive">{{ r.manager }}</span></template>
            <template v-if="r.owner"> · Owner <span class="sensitive">{{ r.owner }}</span></template>
            <template v-if="r.closed"> · resolved {{ fmtDate(r.closed) }}</template>
          </div>
          <p class="sensitive mt-1.5 mb-0 rounded-md bg-green-50 px-3 py-1.5 text-[12px] text-green-900" data-demo-blur><span class="font-semibold">How:</span> {{ r.what_worked }}</p>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { setPriority, logUpdate } from '@/services/api/engagementGraph';
import { titleize } from '@/components/ConfigurablePageComponents/practiceWorkHelpers.js';

const ACTION_STATUS_CLS = { open: 'bg-blue-50 text-blue-700', in_progress: 'bg-amber-50 text-amber-800', resolved: 'bg-green-50 text-green-700' };
const PRO_CHANGE_LABELS = {
  time_or_volume_checkpoint: '2-week time/volume checkpoint',
  workflow_step_design: 'Workflow step design',
  named_practice_owner: 'Named practice owner',
  live_on_call_execution: 'Live on-call execution',
  rule_or_billing_demystification: 'Rule/billing demystified',
  named_patient_walkthrough: 'Named patient walkthrough',
  enabler_prep_paired_with_workflow: 'Prep paired w/ workflow',
};

export default {
  name: 'ConstraintDetail',
  props: {
    constraint: { type: Object, required: true },
    impactModel: { type: Object, default: null },
  },
  data() {
    return {
      logForm: { nodeId: null, text: '', complete: false, saving: false, error: '' },
      ACTION_STATUS_CLS, PRO_CHANGE_LABELS,
    };
  },
  computed: {
    isAction() { return this.constraint.kind === 'action'; },
    resolved() { return this.constraint.solved_elsewhere || []; },
    coaching() { return this.constraint.coaching || []; },
    openCount() { return (this.constraint.actions || []).filter(a => a.status === 'open' || a.status === 'in_progress').length; },
  },
  methods: {
    titleize,
    proChangeTactics(h) {
      return (h.tactics || []).filter(t => t && t.tactic in PRO_CHANGE_LABELS);
    },
    fmtDate(d) {
      if (!d) return '—';
      const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    impactSpec(key) {
      return this.impactModel && this.impactModel.initiatives.find(i => i.initiative === key);
    },
    impactLabel(key) {
      const spec = this.impactSpec(key);
      return spec ? spec.label : titleize(key);
    },
    impactBasis(key) {
      const spec = this.impactSpec(key);
      return spec ? spec.basis : null;
    },
    impactUnit(key) {
      const spec = this.impactSpec(key);
      return spec ? spec.unit : '';
    },
    fmtInt(v) {
      return Math.round(v || 0).toLocaleString();
    },
    priorityTitle(n) {
      if (!n.prioritized) return 'Prioritize';
      return `Prioritized${n.prioritized_by ? ' by ' + n.prioritized_by : ''}${n.prioritized_at ? ' on ' + this.fmtDate(n.prioritized_at) : ''}. Click to clear.`;
    },
    async togglePriority(n) {
      const data = await setPriority(n.id, !n.prioritized);
      n.prioritized = data.prioritized_at != null;
      n.prioritized_at = data.prioritized_at;
      n.prioritized_by = data.prioritized_by_name;
    },
    openLog(nodeId) {
      this.logForm = { nodeId, text: '', complete: false, saving: false, error: '' };
    },
    closeLog() {
      this.logForm = { nodeId: null, text: '', complete: false, saving: false, error: '' };
    },
    async saveLog(target) {
      const f = this.logForm;
      f.saving = true;
      f.error = '';
      try {
        const data = await logUpdate(target.id, f.text.trim(), f.complete);
        target.updates = [data.update, ...(target.updates || [])];
        target.status = data.status;
        this.closeLog();
      } catch {
        f.error = 'Could not save the update.';
        f.saving = false;
      }
    },
  },
};
</script>
